// TableEditor.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { buildTableQrUrl } from '../../utils/tableQrCodes';

// API (your real one)
import * as tablesApi from '../../services/tablesApi';

// utils
const toBool = (v) => {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : v;
  return !(s === 0 || s === '0' || s === false || s === 'false' || s == null);
};
const boolTo10 = (b) => (b ? '1' : '0');

// Fields config (read-only where needed)
const FIELD_CONFIG = [
  { key: 'nickname',       label: 'Nickname' },
  { key: 'number',         label: 'Number' , readOnly: true},
  { key: 'status',         label: 'Status' , readOnly: true},
  { key: 'orderstatus',    label: 'Order Status' , readOnly: true},
  { key: 'occupants',      label: 'Occupants' , readOnly: true},
  { key: 'capacity',       label: 'Capacity' },
  // We'll render 'waiter' via a custom <select>
  { key: 'waiter',         label: 'Waiter' },

  // Read-only fields
  { key: 'guests',         label: 'Guests',           readOnly: true },
  { key: 'messagetime',    label: 'Message Time',     readOnly: true },
  { key: 'attended',       label: 'Attended At',      readOnly: true },
  { key: 'tab_id',         label: 'Active Tab ID',    readOnly: true },
  { key: 'tabType',        label: 'Tab Type',         readOnly: true },

  // Other editable fields
  { key: 'notification',   label: 'Notifications', readOnly: true },
  
  { key: 'isSplit',        label: 'Is Split (1/0)', readOnly: true },
  { key: 'isBillSettled',  label: 'Bill Settled (1/0)' , readOnly: true},
  { key: 'paymentMethod',  label: 'Payment Method', readOnly: true },
];

const diff = (original, edited) => {
  const out = {};
  Object.keys(edited).forEach(k => {
    if (edited[k] !== original[k]) out[k] = edited[k];
  });
  return out;
};

const noop = async () => {};
const confirmAction = (msg) =>
  (typeof window !== 'undefined' && window.confirm) ? window.confirm(msg) : true;

// Try to infer an operator username if not explicitly provided via props
function resolveByWho(explicit) {
  if (explicit) return explicit;
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const ls =
        localStorage.getItem('loggedUser_Username') ||
        localStorage.getItem('loggedUser_Email') ||
        localStorage.getItem('username') ||
        localStorage.getItem('tm.username');
      if (ls) return ls;
    }
  } catch {}
  return 'admin';
}

const TableEditor = ({
  table,
  restaurantId = '1',
  bywho, // optional; if omitted we infer from localStorage (or 'admin')
tenantServerUrl,
  onCancel,

  // You can override these; by default we wire straight to your real API
  onSave = async (changes, ctx) => {
    const result = await tablesApi.editTable(ctx.tableId, ctx.tableNumber, changes);
    console.log('[TableEditor] Save result:', result);
  },
  onOpenTab = async (_form, ctx) => {
    const result = await tablesApi.openTableTab({
      tableId: ctx.tableId,
      number: ctx.tableNumber,
      restaurantId: ctx.restaurantId,
      tabType: _form?.tabType ?? ctx.tabType ?? '', // send if present
      bywho: ctx.bywho,
    });
    console.log('[TableEditor] OpenTab result:', result);
  },
  onCloseTab = async (_form, ctx) => {
    const result = await tablesApi.closeTableTab({
      number: ctx.tableNumber,
      bywho: ctx.bywho,
    });
    console.log('[TableEditor] CloseTab result:', result);
  },
  onForceCloseTable = async (_form, ctx) => {
    const result = await tablesApi.forceCloseTable({
      number: ctx.tableNumber,
      bywho: ctx.bywho,
      waiter: _form?.waiter ?? '',
      total: _form?.total ?? '', // optional; your PHP accepts it, send if you have it
    });
    console.log('[TableEditor] ForceClose result:', result);
  },

  // 🔴 NEW: Delete table hook
  onDeleteTable = async (_form, ctx) => {
    try {
      if (tablesApi && typeof tablesApi.deleteTable === 'function') {
        const result = await tablesApi.deleteTable(tenantServerUrl,{
          tableId: ctx.tableId,
          number: ctx.tableNumber,
          restaurantId: ctx.restaurantId,
          bywho: ctx.bywho,
          
        });
        console.log('[TableEditor] DeleteTable result:', result);
      } else {
        console.warn('[TableEditor] tablesApi.deleteTable not implemented; provide onDeleteTable prop.');
      }
    } catch (err) {
      console.error('[TableEditor] DeleteTable failed:', err);
      throw err;
    }
  },

  // Optional override to supply a custom fetcher in tests or other screens
  fetchEmployees,
}) => {
  const [form, setForm] = useState(table);
  const [visibleChecked, setVisibleChecked] = useState(toBool(table.visibility));
  const [busy, setBusy] = useState(null); // 'open' | 'close' | 'force' | 'delete' | null
  const [qrDataUrl, setQrDataUrl] = useState(null);

  // employees for the Waiter dropdown
  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [empError, setEmpError] = useState(null);

  useEffect(() => {
    setForm(table);
    setVisibleChecked(toBool(table.visibility));
    setBusy(null);
  }, [table?.id]);

  const setVal = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const headerSubtitle = useMemo(() => {
    const tab = table.tab_id ?? '—';
    const ttype = table.tabType ?? '—';
    return `Tab ${tab} (${ttype})`;
  }, [table?.tab_id, table?.tabType]);

  // Button enable logic by status
  const normalizedStatus = (form.status || table.status || '').toString().trim().toLowerCase();
  const isAvailable = normalizedStatus === 'available'; // extend with ['vacant','free'] if desired
  const openDisabled  = busy || !isAvailable;
  const closeDisabled = busy || isAvailable;
  const forceDisabled = !!busy;
  const deleteDisabled = !!busy; // disable delete while any action running

  const qrValue = useMemo(() => {
    const tnum = (form.number ?? table.number ?? table.id ?? '').toString().trim();
    const rid = (restaurantId ?? '').toString().trim();
    return buildTableQrUrl(rid, tnum);
  }, [restaurantId, form.number, table.number, table.id]);

  // Generate QR data URL when qrValue changes
  useEffect(() => {
    let cancelled = false;
    async function gen() {
      if (!qrValue) { setQrDataUrl(null); return; }
      try {
        const QRCode = await import('qrcode'); // npm i qrcode
        const url = await QRCode.toDataURL(qrValue, { margin: 1, scale: 6 });
        if (!cancelled) setQrDataUrl(url);
      } catch (e) {
        console.error('QR generation failed:', e);
        if (!cancelled) setQrDataUrl(null);
      }
    }
    gen();
    return () => { cancelled = true; };
  }, [qrValue]);

  const handleDownloadQR = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `table_qr_${qrValue}.png`;
    a.click();
  };

  // --- Fetch employees for waiter dropdown ---
  useEffect(() => {
    if (!tenantServerUrl && typeof fetchEmployees !== 'function') return;

    let alive = true;
    (async () => {
      try {
        setEmpLoading(true);
        setEmpError(null);

        let raw = [];
        if (typeof fetchEmployees === 'function') {
          raw = await fetchEmployees({ restaurantId });
        } else if (tablesApi && typeof tablesApi.listEmployees === 'function') {
          raw = await tablesApi.listEmployees(tenantServerUrl, { restaurantId });
        } else {
          console.warn('No employees fetcher found. Provide `fetchEmployees` prop or implement tablesApi.listEmployees().');
        }

        // Normalize to { value, label }
        const normalized = (raw || []).map(e => {
          const id = e?.id ?? e?.employee_id ?? e?.user_id ?? e?.username ?? e?.email ?? e?.name;
          const username = e?.username ?? e?.email ?? id;
          const display = e?.display_name ?? e?.name ?? e?.fullname ?? username ?? String(id ?? '');
          const label = username && display && username !== display
            ? `${display} (${username})`
            : (display || username || String(id || ''));
          return {
            value: String(username ?? id ?? ''),
            label,
          };
        }).filter(x => x.value);

        if (alive) setEmployees(normalized);
      } catch (err) {
        console.error('Failed to load employees:', err);
        if (alive) setEmpError('Failed to load employees');
      } finally {
        if (alive) setEmpLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [restaurantId, fetchEmployees, tenantServerUrl]);

  // Ensure current value is present in dropdown even if not returned by API
  const waiterOptions = useMemo(() => {
    const existing = new Map(employees.map(o => [o.value, o]));
    const current = form.waiter ?? table.waiter ?? '';
    if (current && !existing.has(String(current))) {
      existing.set(String(current), { value: String(current), label: String(current) });
    }
    return Array.from(existing.values());
  }, [employees, form.waiter, table.waiter]);

  // --- Common ctx we pass to handlers/APIs ---
  const buildCtx = () => ({
    restaurantId,
    tableId: table?.id ?? form?.id ?? table?.table_id ?? '',
    tableNumber: table?.number ?? form?.number ?? '',
    tabType: table?.tabType ?? form?.tabType ?? '',
    bywho: resolveByWho(bywho),
  });

  // --- Actions (with confirmations) ---
  const label = `Table ${form.number ? `#${form.number}` : form.id}`;

  const doOpen = async () => {
    if (!confirmAction(`Open a new tab for ${label}?`)) return;
    try {
      setBusy('open');
      await onOpenTab(form, buildCtx());
    } finally {
      setBusy(null);
    }
  };

  const doClose = async () => {
    if (!confirmAction(`Close the active tab for ${label}?`)) return;
    try {
      setBusy('close');
      await onCloseTab(form, buildCtx());
    } finally {
      setBusy(null);
    }
  };

  const doForceClose = async () => {
    if (!confirmAction(`Force close ${label}? This may discard open items and unpaid balances.`)) return;
    try {
      setBusy('force');
      await onForceCloseTable(form, buildCtx());
    } finally {
      setBusy(null);
    }
  };

  // 🔴 NEW: Delete handler
  const doDelete = async () => {
    if (!confirmAction(`Permanently delete ${label}? This cannot be undone.`)) return;
    try {
      setBusy('delete');
      await onDeleteTable(form, buildCtx());
      // after successful delete, close the editor
      onCancel?.();
    } catch (err) {
      // error already logged in onDeleteTable, just clear busy
      console.error('[TableEditor] doDelete error:', err);
    } finally {
      setBusy(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const outgoing = { ...form, visibility: boolTo10(visibleChecked) };
    const changes = diff(table, outgoing);
    if (Object.keys(changes).length === 0) { onCancel?.(); return; }
    if (!confirmAction(`Save changes to ${label}?`)) return;
    onSave?.(changes, buildCtx());
  };

  const handleCancel = () => {
    const outgoing = { ...form, visibility: boolTo10(visibleChecked) };
    const changes = diff(table, outgoing);
    if (Object.keys(changes).length > 0) {
      if (!confirmAction('Discard unsaved changes?')) return;
    }
    onCancel?.();
  };

  // Shared input styles
  const inputStyle = {
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 14,
    background: '#fff',
    color: '#111'
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>
          Edit {label}
        </div>
        <div style={{ color: '#666', fontSize: 12 }}>{headerSubtitle}</div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button type="button" onClick={handleCancel}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff' }}>
          Cancel
        </button>
        <button type="submit"
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #0a7', background: '#0c8', color: '#fff' }}>
          Save
        </button>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12, marginTop: 16 }}>
        <button
          type="button"
          onClick={doOpen}
          disabled={!!openDisabled}
          style={{
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid #dfe3e6',
            background: openDisabled ? '#f4f6f8' : '#ffffff',
            textAlign: 'left',
          }}
        >
          {busy === 'open' ? 'Opening…' : 'Open Tab'}
        </button>

        <button
          type="button"
          onClick={doClose}
          disabled={!!closeDisabled}
          style={{
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid #dfe3e6',
            background: closeDisabled ? '#f4f6f8' : '#ffffff',
            textAlign: 'left',
          }}
        >
          {busy === 'close' ? 'Closing…' : 'Close Tab'}
        </button>

        <button
          type="button"
          onClick={doForceClose}
          disabled={!!forceDisabled}
          style={{
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid #dfe3e6',
            background: forceDisabled ? '#f4f6f8' : '#ffffff',
            textAlign: 'left',
          }}
        >
          {busy === 'force' ? 'Forcing Close…' : 'Force Close Table'}
        </button>

        {/* 🔴 NEW: Delete Table button */}
        <button
          type="button"
          onClick={doDelete}
          disabled={!!deleteDisabled}
          style={{
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid #f5b5b5',
            background: deleteDisabled ? '#fce8e8' : '#ffe5e5',
            textAlign: 'left',
            color: '#c00',
            fontWeight: 600,
          }}
        >
          {busy === 'delete' ? 'Deleting…' : 'Delete Table'}
        </button>
      </div>

      {/* Visibility checkbox */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px', marginBottom: 10,
        border: '1px solid #eee', borderRadius: 10, background: '#fafafa'
      }}>
        <input
          id="visibility"
          type="checkbox"
          checked={visibleChecked}
          onChange={(e) => setVisibleChecked(e.target.checked)}
        />
        <label htmlFor="visibility" style={{ userSelect: 'none' }}>
          Show In App (Enable/Disable)
        </label>
      </div>

      {/* Grid fields */}
      <div className="table-editor-fields-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {FIELD_CONFIG.map(({ key, label, readOnly }) => {
          // Custom rendering for Waiter dropdown
          if (key === 'waiter') {
            return (
              <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, color: '#555' }}>
                  {label}
                </span>
                <select
                  value={form.waiter ?? ''}
                  onChange={(e) => setVal('waiter', e.target.value)}
                  disabled={empLoading}
                  style={inputStyle}
                >
                  <option value="">{empLoading ? 'Loading…' : 'Select a waiter'}</option>
                  {empError && <option value="" disabled>Failed to load employees</option>}
                  {waiterOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
            );
          }

          // default text inputs
          return (
            <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: '#555' }}>
                {label}{readOnly ? ' (read-only)' : ''}
              </span>
              <input
                value={form[key] ?? ''}
                onChange={(e) => setVal(key, e.target.value)}
                readOnly={!!readOnly}
                style={{
                  padding: '8px 10px',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  fontSize: 14,
                  background: readOnly ? '#f7f7f7' : '#fff',
                  color: readOnly ? '#777' : '#111'
                }}
                placeholder={label}
              />
            </label>
          );
        })}
      </div>

      {/* QR Code */}
      <div style={{ marginTop: 16, padding: 12, border: '1px solid #eee', borderRadius: 10 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Table QR Code</div>
        <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
          Encodes: <code>{qrValue || '(set restaurantId and table number)'}</code>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 160, height: 160, display: 'grid', placeItems: 'center',
            background: '#fff', border: '1px solid #eee', borderRadius: 8
          }}>
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="Table QR" style={{ width: 140, height: 140 }} />
            ) : (
              <span style={{ fontSize: 12, color: '#999' }}>QR will appear here</span>
            )}
          </div>
          <button
            type="button"
            onClick={handleDownloadQR}
            disabled={!qrDataUrl}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #08c',
              background: qrDataUrl ? '#09f' : '#cfe9ff',
              color: '#fff',
              opacity: qrDataUrl ? 1 : 0.6,
              cursor: qrDataUrl ? 'pointer' : 'not-allowed'
            }}
          >
            Download QR PNG
          </button>
        </div>
      </div>

      {/* Save/Cancel */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button type="button" onClick={handleCancel}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff' }}>
          Cancel
        </button>
        <button type="submit"
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #0a7', background: '#0c8', color: '#fff' }}>
          Save
        </button>
      </div>
    </form>
  );
};

export default TableEditor;
